import axios from 'axios';

const BASE = process.env.REACT_APP_API_BASE || 'https://api.inflection.pro';

const http = axios.create({ baseURL: BASE, timeout: 30000 });

// The API verifies a Clerk session token on every read, so every request has to
// carry one. getToken lives on a React hook and this module is not a component,
// so AuthBridge in App.js hands it over once Clerk has loaded; window.Clerk is
// the fallback for anything that fires before that.
let tokenGetter = null;

export const setTokenGetter = (fn) => {
  tokenGetter = fn;
};

// Routes the API answers without a session. Asking Clerk for a token before
// calling one of these is pure latency on the pages a stranger sees first.
const PUBLIC_PATHS = ['/api/preview'];

// When Clerk's script fails to load, getToken returns a promise that never
// settles rather than rejecting. Awaiting it directly meant one blocked script
// tag silently froze every request in the app - the landing page sat on
// skeletons forever instead of simply rendering signed-out.
const TOKEN_WAIT_MS = 2500;

const tokenOrNothing = async () => {
  const get = tokenGetter || window.Clerk?.session?.getToken?.bind(window.Clerk.session);
  if (!get) return null;
  try {
    return await Promise.race([
      get(),
      new Promise((resolve) => setTimeout(() => resolve(null), TOKEN_WAIT_MS)),
    ]);
  } catch {
    return null; // Signed out. The API answers 401 and the UI says so.
  }
};

http.interceptors.request.use(async (config) => {
  if (PUBLIC_PATHS.some((p) => config.url?.startsWith(p))) return config;
  const token = await tokenOrNothing();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

export const getStatus = () => http.get('/api/status').then((r) => r.data);

export const getSignals = () => http.get('/api/signals').then((r) => r.data);

// The only route that answers without a session. Returns three open trends in
// full, a handful already stripped of their identity, and the landing ticker.
export const getPreview = () => http.get('/api/preview').then((r) => r.data);

export const getEpisode = (id) => http.get(`/api/episodes/${id}`).then((r) => r.data);

export const getCompany = (id) => http.get(`/api/companies/${id}`).then((r) => r.data);

export const listCompanies = () => http.get('/api/companies').then((r) => r.data);

export const getSeries = (id) => http.get(`/api/series/${id}`).then((r) => r.data);

export const getSeriesPoints = (id, weeks = 261) =>
  http.get(`/api/series/${id}/points`, { params: { weeks } }).then((r) => r.data);

// Sparklines for the whole grid in one request rather than one per card.
export const getPointsBulk = (ids, weeks = 52) =>
  http.get('/api/points', { params: { series_ids: ids.join(','), weeks } })
    .then((r) => r.data);

export const search = (q, limit = 25) =>
  http.get('/api/search', { params: { q, limit } }).then((r) => r.data);

export const getFundamentals = (ticker, exchange) =>
  http.get('/api/fundamentals', { params: { ticker, exchange } }).then((r) => r.data);

export default http;
